import React from 'react';
import { Badge, Box, Flex } from '@qga/roo-ui/components';
import PropTypes from 'prop-types';

import Tile from '@shared/propTypes/Tile';
import getSuburbName from '@shared/v2/utils/getSuburbName';

import ConditionsMarkdown from '@/components/ConditionsMarkdown';
import Divider from '@/components/Divider';
import HighlightText from '@/components/HighlightText';
import Summary from '@/components/Summary';
import HolidaysIcon from '@/shared/components/HolidaysIcon';
import ImageGallery from '@/shared/components/ImageGallery';
import getProcuredOfferSash from '@/utils/getProcuredOfferSash';
import sortImages from '@/utils/sortImages';
import PackageInclusions from '@/v2/components/ui/PackageInclusions';
import PropertyTitle from '@/v2/components/ui/PropertyTitle';
import PackageOffer from '../../propTypes/PackageOffer';
import SearchQuery from '../../propTypes/SearchQuery';
import PackageHeroSummary from '../PackageHeroSummary';
import PackageUnavailable from '../PackageUnavailable';
import withSkeleton from '../Skeleton/withSkeleton';
import CallToAction from './components/CallToAction';

const ProcuredOfferOption = ({
  tile,
  packageOffer,
  searchQuery,
  loading,
  Skeleton,
  index,
}) => {
  const {
    procuredOffer,
    propertyContent,
    images,
    mainImage,
    rating,
    ratingType,
  } = tile;
  const { inclusions, description } = procuredOffer.rooms[0].leadInOffers[0];

  const { text, type, icon } = getProcuredOfferSash(procuredOffer);

  const hasOffer = Boolean(searchQuery && packageOffer);

  return (
    <Flex
      flexDirection={['column', null, 'row']}
      borderRadius="default"
      backgroundColor="white"
    >
      <Box width={['100%', null, '600px']} position="relative">
        <ImageGallery
          images={sortImages(images, mainImage)}
          height={['240px', null, '662px']}
          altText={propertyContent.name}
        />
        <Badge
          iconName={icon}
          text={text}
          type={type}
          borderRadius="0 0 10px 0"
          position="absolute"
          top={0}
          left={0}
          fontWeight="bold"
        />
      </Box>
      <Box
        flex="1"
        p={6}
        maxHeight={[null, null, '662px']}
        overflow={[null, null, 'hidden']}
      >
        <Box mb={2}>
          <Summary
            text={getSuburbName(
              propertyContent.suburb,
              propertyContent.destination.title,
            )}
            prefix={<HolidaysIcon />}
          />
        </Box>
        <PropertyTitle
          propertyName={propertyContent.name}
          rating={rating}
          ratingType={ratingType}
          tags={propertyContent.tags || []}
        />

        {procuredOffer.offerDescription && (
          <Box fontSize="md" mb={3}>
            {procuredOffer.offerDescription}
          </Box>
        )}
        {inclusions?.length > 0 ? (
          <PackageInclusions
            inclusions={inclusions}
            procuredOffer={procuredOffer}
            propertyContent={propertyContent}
          />
        ) : (
          description && (
            <Box maxHeight="155px" overflow="hidden" my={-3}>
              <HighlightText fontSize="sm" color="greys.charcoal">
                <ConditionsMarkdown markdown={description} />
              </HighlightText>
            </Box>
          )
        )}
        <Divider my={5} />
        {!!packageOffer || !searchQuery ? (
          <CallToAction
            tile={tile}
            packageOffer={packageOffer}
            searchQuery={searchQuery}
            loading={loading}
            index={index}
          />
        ) : (
          <Box
            pl={4}
            borderLeft={!loading && '3px solid'}
            borderColor={hasOffer ? 'orange' : 'brand.primary'}
          >
            <Skeleton mb="3">
              <PackageHeroSummary
                searchQuery={searchQuery}
                showNights={hasOffer}
              />
            </Skeleton>
            <Skeleton mb="3" lineHeight="1.5" width="100px">
              <PackageUnavailable />
            </Skeleton>
          </Box>
        )}
      </Box>
    </Flex>
  );
};

ProcuredOfferOption.propTypes = {
  loading: PropTypes.bool,
  tile: Tile.isRequired,
  packageOffer: PropTypes.shape(PackageOffer),
  searchQuery: PropTypes.shape(SearchQuery),
  Skeleton: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};

const ProcuredOfferOptionWithSkeleton = withSkeleton(({ loading }) => loading)(
  ProcuredOfferOption,
);

export default ProcuredOfferOptionWithSkeleton;
